// api/notificationPreferences.js
import { useState, useEffect } from "react";
import axios from "utils/axios";

// ==============================|| NOTIFICATION PREFERENCES API ||============================== //

/**
 * Hook to get user notification preferences
 */
export const useGetNotificationPreferences = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchPreferences = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get("/api/users/notification-preferences/");
      setData(response.data);
    } catch (err) {
      setError(err.response?.data?.message || "Erreur lors du chargement des préférences");
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPreferences();
  }, []);

  return {
    preferences: data,
    preferencesLoading: loading,
    preferencesError: error,
    refetchPreferences: fetchPreferences,
  };
};

/**
 * Hook to update user notification preferences
 */
export const useUpdateNotificationPreferences = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const updatePreferences = async (preferences) => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.put("/api/users/notification-preferences/", preferences);
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || "Erreur lors de la sauvegarde des préférences";
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return {
    updatePreferences,
    updateLoading: loading,
    updateError: error,
  };
};

/**
 * Hook to reset notification preferences to defaults
 */
export const useResetNotificationPreferences = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const resetPreferences = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.post("/api/users/notification-preferences/reset/");
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || "Erreur lors de la réinitialisation des préférences";
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return {
    resetPreferences,
    resetLoading: loading,
    resetError: error,
  };
};

/**
 * Default notification preferences structure
 */
export const DEFAULT_NOTIFICATION_PREFERENCES = {
  // Notifications générales
  sound_notifications: true,

  // Notifications de contenu
  new_expert_opinions: true,
  new_news: true,

  // Notifications de modération
  comment_validation: true,
  report_resolution: true,
  comment_deletion: true,
  report_dismissal: true,

  // Notifications INP
  inp_verification_requests: true,
  inp_verification_status: true,
};

/**
 * Notification preference categories for organization
 */
export const NOTIFICATION_CATEGORIES = {
  GENERAL: {
    title: "Paramètres généraux",
    description: "Types de notifications",
    preferences: ['sound_notifications']
  },
  CONTENT: {
    title: "Notifications de contenu",
    description: "Nouveaux contenus",
    preferences: ['new_expert_opinions', 'new_news']
  },
  MODERATION: {
    title: "Notifications de modération",
    description: "Statut de vos contributions",
    preferences: ['comment_validation', 'report_resolution', 'comment_deletion', 'report_dismissal']
  },
  INP: {
    title: "Vérification INP",
    description: "Statut de vérification",
    preferences: ['inp_verification_requests', 'inp_verification_status']
  }
};

/**
 * Email frequency options
 */
export const EMAIL_FREQUENCY_OPTIONS = [];

/**
 * Utility function to validate notification preferences
 */
export const validateNotificationPreferences = (preferences) => {
  const errors = {};
  
  // Check if all required fields are present
  const requiredFields = Object.keys(DEFAULT_NOTIFICATION_PREFERENCES);
  for (const field of requiredFields) {
    if (preferences[field] === undefined) {
      errors[field] = 'Ce champ est requis';
    }
  }
  
  // No email frequency in current UI
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

/**
 * Utility function to merge preferences with defaults
 */
export const mergeWithDefaults = (userPreferences) => {
  return {
    ...DEFAULT_NOTIFICATION_PREFERENCES,
    ...userPreferences
  };
};

/**
 * Utility function to get preference display name
 */
export const getPreferenceDisplayName = (preferenceKey) => {
  const displayNames = {
    sound_notifications: 'Notifications sonores',
    new_expert_opinions: 'Nouveaux avis d\'experts',
    new_news: 'Nouvelles actualités',
    comment_validation: 'Validation de commentaires',
    report_resolution: 'Résolution de signalements',
    comment_deletion: 'Suppression de commentaires',
    report_dismissal: 'Rejet de signalements',
    inp_verification_requests: 'Demandes de vérification',
    inp_verification_status: 'Statut de vérification',
  };
  
  return displayNames[preferenceKey] || preferenceKey;
};

/**
 * Utility function to get preference description
 */
export const getPreferenceDescription = (preferenceKey) => {
  const descriptions = {
    sound_notifications: 'Jouer un son pour les nouvelles notifications',
    new_expert_opinions: 'Être notifié des nouveaux avis d\'experts',
    new_news: 'Être notifié des nouvelles actualités',
    comment_validation: 'Être notifié quand vos commentaires sont approuvés',
    report_resolution: 'Être notifié quand vos signalements sont résolus',
    comment_deletion: 'Être notifié quand vos commentaires sont supprimés',
    report_dismissal: 'Être notifié quand vos signalements sont rejetés',
    inp_verification_requests: 'Être notifié des nouvelles demandes de vérification INPE',
    inp_verification_status: 'Être notifié des changements de statut de votre vérification',
  };
  
  return descriptions[preferenceKey] || '';
};
